import { AsyncMethodReturns, Connection, connectToChild } from 'penpal'
import { IframeMethods } from '../iframe'
import { getHostMethods, HostMethods } from './getHostMethods'
import { notifyHostReady, setupReadyCheckListener } from '../handshake'
import { createVisibilityTracker, getHostCapabilities, VisibilityTracker } from './visibilityTracker'
import { VisibilitySample } from '../visibility'

// Samples produced before the Penpal connection resolves are buffered; bound the buffer so a
// handshake that never completes (60 s timeout) cannot grow it without limit.
const MAX_PENDING_SAMPLES = 20

export type IframeClient = AsyncMethodReturns<IframeMethods>
export interface IframeClientContext {
  element: HTMLIFrameElement
  /** Penpal only accepts messages from the origin the iframe had at connect time. */
  origin: string | null
  connection: Connection<IframeMethods>
  hostMethods: HostMethods
  /** Null while the handshake is in flight. */
  client: IframeClient | null
  readyCheckListenerCleanup: () => void
  tracker: VisibilityTracker | null
}

function getIframeOrigin(iframe: HTMLIFrameElement): string | null {
  try {
    return new URL(iframe.src).origin
  } catch {
    return null
  }
}

function isDebugEnabled() {
  try {
    return localStorage.getItem('lectrium-debug') === 'true'
  } catch {
    return false
  }
}

export class HostIntegrationManager {
  private _iframes = new Map<HTMLIFrameElement, IframeClientContext>()

  private _lastUrl: string | null = null

  async setIframes(iframes: HTMLIFrameElement[]): Promise<void> {
    const newIframes = new Set(iframes)

    // Same-origin navigations reconnect inside Penpal; a move to another origin needs a new connection.
    for (const [iframe, context] of this._iframes) {
      if (newIframes.has(iframe) && context.origin === getIframeOrigin(iframe)) continue
      this.removeIframe(iframe)
    }

    const promises: Promise<void>[] = []
    for (const iframe of newIframes) {
      if (!this._iframes.has(iframe)) {
        promises.push(this.addIframe(iframe))
      }
    }
    await Promise.all(promises)
  }

  private async addIframe(iframe: HTMLIFrameElement): Promise<void> {
    const element = iframe.contentWindow
    if (!element) {
      console.warn('[Lectrium] Iframe has no contentWindow, skipping integration', iframe)
      return
    }

    // Late-bound client reference — populated after Penpal connection resolves.
    // Lets hostMethods.enableViewportTracking reach client.updateViewport once the iframe is connected.
    let clientRef: IframeClient | null = null
    const hostMethods = getHostMethods(
      iframe,
      state => {
        clientRef?.updateViewport(state)
      },
      state => {
        void clientRef?.updateHostConsent(state)
      }
    )
    const isDebug = isDebugEnabled()

    // Start observing before the handshake so the badge's first impression is captured while it
    // connects; samples arriving before the connection resolves are buffered and flushed on connect.
    const pending: VisibilitySample[] = []
    const tracker = createVisibilityTracker(iframe, sample => {
      if (clientRef) {
        void clientRef.updateVisibility(sample)
        return
      }
      pending.push(sample)
      if (pending.length > MAX_PENDING_SAMPLES) pending.shift()
    })

    // For Penpal to operate correctly, we must ensure that connectToChild is called before the iframe calls connectToParent.
    const connection = connectToChild<IframeMethods>({
      iframe,
      methods: hostMethods,
      debug: isDebug,
    })

    const readyCheckListenerCleanup = setupReadyCheckListener(iframe)
    notifyHostReady(iframe)

    // Registered before the handshake so a src change mid-handshake can tear this connection down.
    const context: IframeClientContext = {
      element: iframe,
      origin: getIframeOrigin(iframe),
      connection,
      hostMethods,
      client: null,
      readyCheckListenerCleanup,
      tracker,
    }
    this._iframes.set(iframe, context)
    const isCurrent = () => this._iframes.get(iframe) === context

    let client: IframeClient
    try {
      client = await connection.promise
    } catch {
      // Destroyed while handshaking (removed, or moved to another origin).
      return
    }
    if (!isCurrent()) return

    const who = await client.who()
    if (!isCurrent()) return

    if (who !== 'lectrium-dashboard-hub') {
      console.warn('[Lectrium] mismatching iframe contract, skipping', iframe)
      this.removeIframe(iframe)
      return
    }

    clientRef = client
    context.client = client

    // Report capabilities even when there is no tracker, so the iframe can emit
    // `badge_visibility_unsupported` when the host has no IntersectionObserver.
    void client.setHostCapabilities(getHostCapabilities())
    for (const sample of pending.splice(0)) void client.updateVisibility(sample)

    // Read at connect, not at script load: SPA hosts create the badge after client-side navigation.
    this._lastUrl = window.location.href
    void client.updateUrl(this._lastUrl)
  }

  /** Some sites call replaceState on every scroll; an unchanged URL is not sent again. */
  public setUrl(newUrl: string): void {
    if (newUrl === this._lastUrl) return
    this._lastUrl = newUrl
    for (const client of this.getClients()) {
      void client.updateUrl(newUrl)
    }
  }

  private removeIframe(iframe: HTMLIFrameElement): void {
    const config = this._iframes.get(iframe)
    config?.tracker?.stop()
    if (config?.connection) {
      config.connection.destroy()
    }
    config?.readyCheckListenerCleanup()
    this._iframes.delete(iframe)
  }

  public getClients(): IframeClient[] {
    return Array.from(this._iframes.values()).flatMap(x => (x.client ? [x.client] : []))
  }

  public getClient(iframe: HTMLIFrameElement): IframeClient | undefined {
    return this._iframes.get(iframe)?.client ?? undefined
  }
}
